/**
 * @fileOverview Scenes: stretches of steps that share a state of the page — a dialog open, a filter
 * applied. Entering one, catching up inside one after a jump, and leaving one. See `ViewHelpScene`.
 */

import type { ViewHelpStep } from "@/services/viewHelp/types"
import { ESCAPE_RETRY_MS, sleep } from "./timing"
import { Move, frameWindow, skipped } from "./state"
import { steps, targetOf, tour } from "./tour"
import { holdOnPage, holdOnRegion, releaseHold } from "./camera"
import { runActions, runActionsQuietly } from "./actions"
import { pressEscape } from "./input"
import { reloadFrameOnto } from "./frame"

/**
 * The scene the frame is in, and the furthest of its steps whose actions have run. Not reactive:
 * nothing draws them, they are the player's memory of what state it has put the page into.
 */
let activeScene: string | null = null
let sceneReached = -1

/**
 * Whether the page may be somewhere between two states of the active scene: its `enter` or a step's
 * actions began and did not finish — the move was overtaken, or an action found nothing to press.
 *
 * `sceneReached` counts only actions that **finished**. It used to be set before they ran, so that an
 * overtaking move would know to run the exit; but the move that caught up then took those actions as
 * done. A seek from "Suche" onto "Anhaken" skipped the search press that had been overtaken, found no
 * rows to tick, and took every later step of the wizard down with it. The exit is still known to be
 * needed — `activeScene` is set before anything runs — and a scene in this state is started over.
 */
let sceneUncertain = false

/** The irreversible scenes that have run on the frame's current page. Cleared when the frame reloads. */
const playedIrreversible = new Set<string>()

/** Forgets what irreversible scenes did — for a frame that has just been loaded afresh. */
export function forgetPlayedScenes() {
  playedIrreversible.clear()
}

const sceneOf = (key: string | null | undefined) =>
  key ? (tour.value?.scenes?.find(scene => scene.key === key) ?? null) : null

/** Whether the frame is in a scene that `candidate` is not part of — and has to be left first. */
export const isInAnotherScene = (candidate: ViewHelpStep) =>
  activeScene !== null && activeScene !== (candidate.scene ?? null)

/**
 * Whether the page holds more of `candidate`'s scene than `candidate` itself: a later step's input, or a
 * state between two steps. Such a step cannot simply be pointed at again — its scene has to be replayed.
 */
export const sceneIsAheadOf = (candidate: ViewHelpStep, index: number) =>
  activeScene !== null && activeScene === (candidate.scene ?? null) && (sceneUncertain || sceneReached > index)

/** Forgets the scene without leaving it — for a frame that was reloaded or thrown away with it. */
export function forgetScene() {
  activeScene = null
  sceneReached = -1
  sceneUncertain = false
}

/**
 * Takes the frame out of the scene it is in.
 *
 * The scene's own `exit` first — the pointer pressing "Abbrechen" is part of the story — unless the
 * walkthrough is leaving the view and the scene `endsWithView`. The scene is only forgotten once its
 * exit has run to the end. Then, as a backstop, Escape for as long
 * as any overlay is still open: a scene must never leave a dialog or a menu over the next step,
 * whatever went wrong inside it.
 */
export async function leaveScene(move: Move, animate: boolean, leavingView = false) {
  const scene = sceneOf(activeScene)
  if (scene && !(leavingView && scene.endsWithView)) {
    sceneUncertain = true
    await runActionsQuietly(scene.exit, move, animate)
    // Overtaken halfway through the exit: the page is somewhere between the scene and out of it. The
    // scene stays the active one, so the move that overtook this one runs the exit again — every exit
    // is safe to repeat. Forgotten first, as it once was, an exit cut short was never finished: the cost
    // page stayed on the week view, and the heatmap's steps found nothing and dropped off the timeline.
    if (!move.current) return
  }
  forgetScene()

  // The planning board's context menu counts as an overlay here even though it is not a Vuetify one:
  // it is drawn over the page just the same, and `pressEscape` knows how to dismiss it.
  const win = frameWindow.value
  for (let attempt = 0; win && attempt < 3; attempt++) {
    if (!win.document.querySelector(".v-overlay--active, .context-menu-overlay")) break
    pressEscape(win)
    await sleep(ESCAPE_RETRY_MS)
  }
}

/**
 * Puts the frame into the state `candidate` is explained in: into its scene, through every earlier
 * step of that scene, then its own actions. False when that could not be done, and the step is
 * skipped — and the scene is left marked uncertain, so the next step in it starts it over rather than
 * building on a state that never came about.
 *
 * What is played and what is only done: the step's **own** demonstration is always played, pointer and
 * all — its actions, and the scene's `enter` when this step is the one that opens the scene. Only the
 * way there is done at speed: the earlier steps of the scene a seek jumps into the middle of. A seek
 * skips the road, not the step; one that also skipped the step flipped the comparison switch on
 * Auswertung with nobody's hand on it.
 *
 * The scene is marked as entered *before* its `enter` runs — a move can be overtaken halfway, and the
 * one that overtakes it must still know to run `exit`, or a half-opened dialog would stay on screen
 * over whatever the reader jumped to. A step is marked as reached only once its actions have finished
 * (see `sceneUncertain`).
 */
export async function prepareScene(candidate: ViewHelpStep, index: number, move: Move) {
  const key = candidate.scene ?? null
  if (!key) return true

  const pageOnly = candidate.zoom === false
  if (pageOnly) holdOnPage()
  try {
    return await playScene(candidate, key, index, move, pageOnly)
  } finally {
    releaseHold()
  }
}

/** The body of `prepareScene`, with the camera hold for a `zoom: false` step already in place. */
async function playScene(candidate: ViewHelpStep, key: string, index: number, move: Move, pageOnly: boolean) {
  const scene = sceneOf(key)
  if (!scene) return false

  // Back inside the scene — the page already holds later steps' input — or the page is somewhere
  // between two of its states: either way the scene starts over.
  const restarting = activeScene === key && (index <= sceneReached || sceneUncertain)
  // An irreversible scene that has already run on this page cannot start over on it (see
  // `ViewHelpScene.irreversible`): whether the reader went back inside it or came back into it from
  // outside, it starts over on a freshly loaded page instead.
  if (scene.irreversible && playedIrreversible.has(key) && (restarting || activeScene !== key)) {
    forgetScene()
    if (!(await reloadFrameOnto(targetOf(candidate), true))) return false
  } else if (restarting) {
    await leaveScene(move, false)
  }
  if (!move.current) return false

  if (activeScene !== key) {
    const opening = steps.value.findIndex(step => step.scene === key)
    activeScene = key
    sceneReached = opening - 1
    sceneUncertain = true
    if (scene.irreversible) playedIrreversible.add(key)
    // Played when this step opens the scene — then it is this step's demonstration — and done at
    // speed when a jump lands further in.
    if (!(await runActions(scene.enter, move, index === opening))) return false
    sceneUncertain = false
  }

  // A jump into the middle of the scene: the steps in between happen first, at speed.
  for (let i = sceneReached + 1; i < index; i++) {
    const earlier = steps.value[i]
    if (earlier.scene !== key || !earlier.actions || skipped.value.has(i)) continue
    sceneUncertain = true
    if (!(await runActions(earlier.actions, move, false))) return false
    sceneReached = i
    sceneUncertain = false
  }

  if (!candidate.actions) {
    sceneReached = index
    return true
  }

  // The step's own actions play inside its region: the camera frames the whole group once, and holds —
  // unless the step keeps to the whole page, which is already being held.
  sceneUncertain = true
  if (!pageOnly) await holdOnRegion(candidate, move)
  if (!(await runActions(candidate.actions, move, true))) return false
  sceneReached = index
  sceneUncertain = false
  return true
}
